import { useState, useRef, useCallback } from 'react';
import { UsernameCache, PendingUsernameRequest } from '../types/app.types';

interface UsernameHookProps {
  sendMessage: (message: any) => void;
}

export const useUsername = ({ sendMessage }: UsernameHookProps) => {
  // Initialize username from localStorage or generate a default one
  const [username, setUsername] = useState(() => {
    return localStorage.getItem('username') || `User_${Math.floor(Math.random() * 10000)}`;
  });
  
  const [usernameCache, setUsernameCache] = useState<UsernameCache>(new Map());
  const [isAdmin, setIsAdmin] = useState(false);
  const pendingUsernameRequests = useRef(new Map<string, PendingUsernameRequest>());

  const handleUsernameChange = useCallback((newUsername: string) => {
    setUsername(newUsername);
    localStorage.setItem('username', newUsername);
    
    // Send username update to server
    sendMessage({
      type: 'set_username',
      username: newUsername
    });
  }, [sendMessage]);

  const handleUsernameResponse = useCallback((userId: string, username: string, requestId: string) => {
    // Update cache
    setUsernameCache(prev => new Map(prev).set(userId, username));
    
    // Handle pending popup
    const pendingRequest = pendingUsernameRequests.current.get(requestId);
    if (pendingRequest && pendingRequest.layer) {
      if (pendingRequest.basePopupText) {
        // For CustomGeoJSON markers
        pendingRequest.layer.setPopupContent(pendingRequest.basePopupText + `<br/><small>Created by: ${username}</small>`);
      } else {
        // For DrawnLayers
        pendingRequest.layer.bindPopup(`Created by: ${username}`).openPopup();
      }
      pendingUsernameRequests.current.delete(requestId);
    }
  }, []);

  const registerPendingRequest = useCallback((requestId: string, data: PendingUsernameRequest) => {
    pendingUsernameRequests.current.set(requestId, data);
  }, []);

  const handleUserList = useCallback((users: Array<{ id: string; username: string }>) => {
    users.forEach((user) => {
      if (user.id && user.username) {
        setUsernameCache(prev => new Map(prev).set(user.id, user.username));
      }
    });
  }, []);

  const handleAdminStatusResponse = useCallback((isAdminStatus: boolean) => {
    setIsAdmin(isAdminStatus);
    console.log(`Admin status: ${isAdminStatus}`);
  }, []);

  return {
    username,
    usernameCache,
    isAdmin,
    handleUsernameChange,
    handleUsernameResponse,
    registerPendingRequest,
    handleUserList,
    handleAdminStatusResponse
  };
};